"use client";
import React, { useEffect, useState } from "react";
import Switch from "@mui/material/Switch";
import { landingApi } from "../api/landingApi";
import { ADDONS } from "../api/addons";
import { useNavigate } from "react-router-dom";
import { ChevronDownIcon, MinusCircle, PlusCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const PriceCalculator = ({ setShowCalculator }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isUSD, setIsUSD] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("Six-Monthly");
  const [allChecked, setAllChecked] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState("Premium");
  const [quantities, setQuantities] = useState([]);

  const [partnershipDiscount, setPartnershipDiscount] = useState(true);
  const [discountPercentage, setDiscountPercentage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setPlansData] = useState([]);
  const [addonsData, setAddonsData] = useState([]);

  const label = { inputProps: { "aria-label": "Switch demo" } };

  const getAddonPrice = (addon) => {
    if (addon.price !== undefined) {
      return addon.price;
    }
    const prices = addon.prices || {};
    switch (selectedOption) {
      case "Three-Monthly":
        return prices.price_3month || 0;
      case "Six-Monthly":
        return prices.price_6month || 0;
      case "Twelve-Monthly":
        return prices.price_12month || 0;
      default:
        return prices.price_1month || 0;
    }
  };

  const tableData = Array.isArray(addonsData)
    ? addonsData.map((addon, index) => {
        const unitPrice = getAddonPrice(addon);
        return {
          feature: t(
            addon.name?.toLowerCase().replace(/\s+/g, "") || "unknown",
          ),
          unit: addon.unit || t("unit"),
          quantity: quantities[index] || 0,
          amount: unitPrice,
          total: (quantities[index] || 0) * unitPrice,
        };
      })
    : [];

  const packagePrices = {
    Basic: 120000,
    Standard: 240000,
    Premium: 390000,
  };

  const handleHeaderCheckboxChange = () => {
    const newAllChecked = !allChecked;
    setAllChecked(newAllChecked);

    const newQuantities = quantities.map((quantity, index) => {
      if (index === 6) return 0;
      return newAllChecked ? (quantity > 0 ? quantity : 1) : 0;
    });

    setQuantities(newQuantities);
  };

  const options = ["Three-Monthly", "Six-Monthly", "Twelve-Monthly"];

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setIsOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plans, addons] = await Promise.all([
          landingApi.fetchPlans(),
          landingApi.fetchAddons(),
        ]);
        console.log("addons data: ", addons);
        console.log("getplan data: ", plans);

        const addonList = addons && addons.length > 0 ? addons : ADDONS;
        setAddonsData(addonList);
        setPlansData(plans || []);

        if (addonList.length > 0) {
          setQuantities(Array(addonList.length).fill(0));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setAddonsData(ADDONS);
        setQuantities(Array(ADDONS.length).fill(0));
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const subtotal =
    tableData.reduce((sum, item) => sum + item.total, 0) +
    packagePrices[selectedPackage];
  const tax = subtotal * 0.05;
  const discountAmount = partnershipDiscount
    ? subtotal * ((discountPercentage * 2) / 100)
    : 0;
  const total = subtotal + tax - discountAmount;

  const handleDiscountIncrease = () => {
    setDiscountPercentage((prev) => Math.min(10, prev + 1));
  };

  const handleDiscountDecrease = () => {
    setDiscountPercentage((prev) => Math.max(3, prev - 1));
  };

  const handleQuantityChange = (index, newQuantity) => {
    const newQuantities = [...quantities];
    newQuantities[index] = newQuantity;
    setQuantities(newQuantities);
  };

  const handleRowCheckboxChange = (index, isChecked) => {
    const newQuantities = [...quantities];
    if (index === 6) {
      newQuantities[index] = 0;
    } else {
      newQuantities[index] = isChecked
        ? newQuantities[index] > 0
          ? newQuantities[index]
          : 1
        : 0;
    }
    setQuantities(newQuantities);

    const allRowsChecked = newQuantities.every((qty, i) =>
      i === 6 ? true : qty > 0,
    );
    setAllChecked(allRowsChecked);
  };

  if (loading) {
    return <div className="p-6 text-center">{t("loading")}</div>;
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-500">
        {t("error")}: {error}
      </div>
    );
  }

  return (
    <div className="relative max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <X
        color="red"
        className="custom-rounded-button custom-box-shadow absolute right-3"
        size={25}
        onClick={() => setShowCalculator(false)}
      />
      <div className="border-gray-300 py-2 border-b flex gap-3 justify-between items-center my-3">
        <h2 className="text-2xl font-bold text-center font-playfair">
          {t("priceCalculator")}
        </h2>
        <div className="flex justify-between items-center">
          <span
            className={`text-xl font-semibold ${
              isUSD ? "text-blue-600" : "text-gray-400"
            }`}
          >
            USD
          </span>
          <Switch
            {...label}
            checked={!isUSD}
            onChange={() => setIsUSD(!isUSD)}
          />
          <span
            className={`text-xl font-semibold ${
              !isUSD ? "text-blue-600" : "text-gray-400"
            }`}
          >
            {t("localCurrency")}
          </span>
        </div>
      </div>

      <div className="flex py-3 px-5 justify-between my-3 items-center">
        <h3 className="text-2xl font-semibold">{t("paymentPlan")}</h3>
        <div className="relative">
          <div className="flex bg-[#485E92] text-white rounded-full overflow-hidden">
            <span
              className="px-4 py-2 border-r-4 cursor-pointer"
              onClick={() => setIsOpen(!isOpen)}
            >
              {t(selectedOption.toLowerCase().replace("-", ""))}
            </span>
            <button
              className="px-2 py-2 bg-[#485E92]"
              onClick={() => setIsOpen(!isOpen)}
            >
              <ChevronDownIcon size={20} />
            </button>
          </div>

          {isOpen && (
            <div className="absolute top-full right-0 mt-2 bg-white text-gray-800 rounded-lg shadow-lg z-10 min-w-full">
              {options.map((option) => (
                <div
                  key={option}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                    selectedOption === option ? "bg-blue-100 font-semibold" : ""
                  }`}
                  onClick={() => handleOptionSelect(option)}
                >
                  {t(option.toLowerCase().replace("-", ""))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex border border-gray-300 rounded-lg overflow-hidden mb-6">
        <div className="flex w-2/3 px-4 py-3 border-r border-gray-300 justify-between items-center bg-[#EBF2F5]">
          <div className="flex flex-col">
            <h2 className="text-xl font-semibold text-center">
              {t("package")}
            </h2>
            <div className="flex gap-2 mt-1">
              {["Basic", "Standard", "Premium"].map((pkg) => (
                <button
                  key={pkg}
                  className={`px-3 py-1 rounded-full text-sm ${
                    selectedPackage === pkg
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() => setSelectedPackage(pkg)}
                >
                  {t(pkg.toLowerCase())}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="w-1/3 flex items-center justify-center bg-[#EBF2F5]">
          <span className="font-bold text-2xl">
            {packagePrices[selectedPackage].toLocaleString()}
          </span>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-3">{t("addOnFeatures")}</h3>
        <table className="w-full rounded-lg overflow-hidden border-collapse border border-gray-300">
          <thead className="bg-white text-gray-500">
            <tr>
              <th className="p-2 w-12 border">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4"
                  checked={allChecked}
                  onChange={handleHeaderCheckboxChange}
                />
              </th>
              <th className="p-2 text-left border">{t("addOnFeatures")}</th>
              <th className="p-2 text-left border">{t("unit")}</th>
              <th className="p-2 text-left border">{t("quantity")}</th>
              <th className="p-2 text-left border">{t("amount")}</th>
            </tr>
          </thead>
          <tbody className="bg-[#EBF2F5]">
            {tableData.map((data, index) => (
              <tr key={index} className="border-b border-blue-200">
                <td className="p-2 border border-gray-200 text-center">
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-blue-600"
                    defaultChecked
                    checked={quantities[index] > 0}
                    onChange={(e) =>
                      handleRowCheckboxChange(index, e.target.checked)
                    }
                  />
                </td>
                <td
                  className="p-2 border border-gray-200 cursor-pointer hover:text-blue-600"
                  onClick={() =>
                    handleRowCheckboxChange(index, quantities[index] <= 0)
                  }
                >
                  {data.feature}
                </td>
                <td className="p-2 border border-gray-200">{data.unit}</td>
                <td className="p-2 border border-gray-200 flex gap-3 justify-center items-center">
                  <MinusCircle
                    className="cursor-pointer active:scale-90 hover:shadow-md rounded-full transition-all duration-200"
                    onClick={() =>
                      handleQuantityChange(
                        index,
                        Math.max(0, data.quantity - 1),
                      )
                    }
                  />
                  <input
                    type="number"
                    min="0"
                    value={data.quantity}
                    onChange={(e) =>
                      handleQuantityChange(index, parseInt(e.target.value) || 0)
                    }
                    className="w-12 p-1 border border-gray-300 rounded text-center"
                  />
                  <PlusCircle
                    className="cursor-pointer shadow-sm hover:shadow-md rounded-full transition-all duration-200 active:scale-90"
                    onClick={() =>
                      handleQuantityChange(index, data.quantity + 1)
                    }
                  />
                </td>
                <td className="p-2 border border-gray-200">
                  {quantities[index] > 0 ? data.total.toLocaleString() : "0"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-gray-300 pt-4 mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-lg">{t("total")}</span>
          <span className="text-lg font-semibold">
            {subtotal.toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-lg">{t("tax")} (5%)</span>
          <span className="text-lg">{tax.toLocaleString()}</span>
        </div>
      </div>

      <div className="flex items-start gap-3 mb-6 p-4 bg-[#EBF2F5] rounded-lg">
        <Switch
          checked={partnershipDiscount}
          onChange={(e) => setPartnershipDiscount(e.target.checked)}
          color="primary"
        />
        <div className="flex-grow">
          <h5 className="font-semibold text-lg">{t("partnershipDiscount")}</h5>
          <p className="text-sm text-gray-600 mb-2">
            {t("partnershipDiscountDesc")}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm mr-2">
              {t("discountOfferToMemberCustomers")}
            </span>
            <button
              onClick={handleDiscountDecrease}
              className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded-full hover:bg-gray-300"
              disabled={!partnershipDiscount || discountPercentage <= 3}
            >
              -
            </button>
            <span className="w-8 text-center font-semibold">
              {discountPercentage}%
            </span>
            <button
              onClick={handleDiscountIncrease}
              className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded-full hover:bg-gray-300"
              disabled={!partnershipDiscount || discountPercentage >= 10}
            >
              +
            </button>
          </div>
        </div>
        <span className="text-lg font-semibold text-red-600">
          -{discountAmount.toLocaleString()}
        </span>
      </div>

      <div className="flex justify-between items-center p-4 bg-[#EBF2F5] rounded-lg">
        <h3 className="text-xl font-bold">{t("totalPrice")}</h3>
        <span className="text-2xl font-bold">{total.toLocaleString()}</span>
      </div>
      <div className="flex justify-end mt-4">
        <button
          onClick={() => {
            const selectedAddons = addonsData.filter(
              (_, index) => quantities[index] > 0,
            );
            const billingData = {
              selectedPackage,
              selectedOption,
              packagePrice: packagePrices[selectedPackage] || 0,
              addons: selectedAddons.map((addon, index) => ({
                ...addon,
                quantity: quantities[index] || 0,
                total: (quantities[index] || 0) * getAddonPrice(addon),
              })),
              subtotal,
              tax,
              discountAmount,
              totalAmount: total,
              billingCycle:
                selectedOption === "Three-Monthly"
                  ? 3
                  : selectedOption === "Six-Monthly"
                    ? 6
                    : 12,
            };

            localStorage.setItem("selectedPlan", JSON.stringify(billingData));
            localStorage.setItem("paymentAmount", total);
            localStorage.setItem("paymentAddonType", "subscription");

            setShowCalculator(false);
            navigate("/billing");
          }}
          className="px-4 py-3 custom-darkblue-button play-fair custom-shadow-effect"
        >
          {t("proceedToBilling")}
        </button>
      </div>
    </div>
  );
};

export default PriceCalculator;
